import { useState, useEffect } from 'react';
import { hrAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { DAY_NAMES, SHIFT_TYPES } from './scheduleUtils';

const empty = (emp, iso) => ({
  employee_id: emp?.id || '',
  type: 'shift',
  start_time: '09:00',
  end_time: '17:00',
  label: '',
  notes: '',
  recurring: true,
  day_of_week: iso ? (new Date(iso + 'T00:00:00').getDay() + 6) % 7 : 0,
  effective_from: iso || '',
  effective_to: '',
  date: iso || '',
});

// Create (emp + iso) or edit (shift) a shift definition. Admin only.
export const ShiftDialog = ({ open, onClose, employees, emp, iso, shift, onSaved }) => {
  const [form, setForm] = useState(empty(emp, iso));
  const [saving, setSaving] = useState(false);
  const editing = !!shift;

  useEffect(() => {
    if (!open) return;
    setForm(shift ? { ...empty(null, shift.date), ...shift, label: shift.label || '', notes: shift.notes || '', effective_to: shift.effective_to || '', date: shift.date || '' } : empty(emp, iso));
  }, [open, shift, emp, iso]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.employee_id) return toast.error('Pick an employee');
    setSaving(true);
    const payload = {
      type: form.type, start_time: form.start_time, end_time: form.end_time,
      label: form.label || null, notes: form.notes || null,
      ...(form.recurring
        ? { day_of_week: Number(form.day_of_week), effective_from: form.effective_from || null, effective_to: form.effective_to || null }
        : { date: form.date }),
    };
    try {
      if (editing) await hrAPI.updateShift(shift.shift_id || shift.id, payload);
      else await hrAPI.createShift({ ...payload, employee_id: form.employee_id, recurring: form.recurring });
      toast.success(editing ? 'Shift updated — employee notified' : 'Shift added — employee notified');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save shift');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(shift.recurring ? 'Delete this recurring shift for all weeks?' : 'Delete this shift?')) return;
    setSaving(true);
    try {
      await hrAPI.deleteShift(shift.shift_id || shift.id);
      toast.success('Shift deleted — employee notified');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete shift');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="shift-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Edit shift' : 'Add shift'}</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          <div>
            <Label>Employee</Label>
            <Select value={form.employee_id} onValueChange={(v) => set('employee_id', v)} disabled={editing}>
              <SelectTrigger className="mt-1" data-testid="shift-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger className="mt-1" data-testid="shift-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SHIFT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Start</Label><Input type="time" className="mt-1" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} data-testid="shift-start-input" /></div>
            <div><Label>End</Label><Input type="time" className="mt-1" value={form.end_time} onChange={(e) => set('end_time', e.target.value)} data-testid="shift-end-input" /></div>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
            <div>
              <p className="text-sm font-medium text-slate-800">Repeat weekly</p>
              <p className="text-xs text-slate-500">Same day every week until an end date</p>
            </div>
            <Switch checked={form.recurring} onCheckedChange={(v) => set('recurring', v)} disabled={editing} data-testid="shift-recurring-switch" />
          </div>
          {form.recurring ? (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Day</Label>
                <Select value={String(form.day_of_week)} onValueChange={(v) => set('day_of_week', Number(v))}>
                  <SelectTrigger className="mt-1" data-testid="shift-day-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DAY_NAMES.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>From</Label><Input type="date" className="mt-1" value={form.effective_from} onChange={(e) => set('effective_from', e.target.value)} data-testid="shift-from-input" /></div>
              <div><Label>Until <span className="text-slate-400">(optional)</span></Label><Input type="date" className="mt-1" value={form.effective_to} onChange={(e) => set('effective_to', e.target.value)} data-testid="shift-until-input" /></div>
            </div>
          ) : (
            <div><Label>Date</Label><Input type="date" className="mt-1" value={form.date} onChange={(e) => set('date', e.target.value)} data-testid="shift-date-input" /></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Label <span className="text-slate-400">(optional)</span></Label><Input className="mt-1" placeholder="e.g. Front desk" value={form.label} onChange={(e) => set('label', e.target.value)} data-testid="shift-label-input" /></div>
            <div><Label>Notes <span className="text-slate-400">(optional)</span></Label><Input className="mt-1" value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="shift-notes-input" /></div>
          </div>
        </div>
        <DialogFooter className="flex sm:justify-between gap-2">
          {editing ? (
            <Button variant="ghost" className="text-red-600 hover:bg-red-50" onClick={remove} disabled={saving} data-testid="shift-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={save} disabled={saving} data-testid="shift-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{editing ? 'Save changes' : 'Add shift'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
